import React, { useMemo } from "react"
import { useSnapshot } from "valtio"
import { Steps } from "../../../../constants"
import { IMAGE_SERVER } from "../../../../config"
import { wizardStore } from "../../../../stores/wizard"
import { CircleData, LeatherIndicators, StepOption } from "../../../../types/wizard"
import GeneralInfo from "./GeneralInformation"
import { isEmpty } from "../../../../utils/general"
import Loading from "../../../commons/Loading"
import { useMedia } from "../../../../hooks/useMedia";
import './../../../../App.css';

export default function RequestDetailsSummary() {
  const wizSnap = useSnapshot(wizardStore)
  const matchMedia = useMedia("(max-width: 768px)");
  const designTypeImage = useMemo(() => wizSnap.emblemData?.[Steps.DesignType], [wizSnap.emblemData])
  const specialInstructions = useMemo(() => wizSnap.designRequest?.createDesign?.specialInstructions, [wizSnap.designRequest])
  const holographicUploadImage = useMemo(() => wizSnap.emblemData?.[Steps.HolographicUpload], [wizSnap.emblemData])
  const shape = useMemo(() => wizSnap.emblemData?.[Steps.Shape], [wizSnap.emblemData])
  const sizeStandard = useMemo(() => wizSnap.emblemData?.[Steps.SizeStandard], [wizSnap.emblemData])
  const fabricColor = useMemo(() => wizSnap.emblemData?.[Steps.FabricColor], [wizSnap.emblemData])
  const fabricType = useMemo(() => wizSnap.emblemData?.[Steps.FabricType], [wizSnap.emblemData])
  const backing = useMemo(() => wizSnap.emblemData?.[Steps.Backing], [wizSnap.emblemData])
  const borderType = useMemo(() => wizSnap.emblemData?.[Steps.BorderType], [wizSnap.emblemData])
  const borderColor = useMemo(() => wizSnap.emblemData?.[Steps.BorderColor], [wizSnap.emblemData])
  const layout = useMemo(() => wizSnap.emblemData?.[Steps.Layout], [wizSnap.emblemData])
  const colors = useMemo(() => wizSnap.emblemData?.[Steps.ThreadColors], [wizSnap.emblemData])
  const emblem3d = useMemo(() => wizSnap.emblemData?.[Steps.Emblem3d], [wizSnap.emblemData])
  const flipColors = useMemo(() => wizSnap.emblemData?.[Steps.HolographicColors], [wizSnap.emblemData])
  const bgPattern = useMemo(() => wizSnap.emblemData?.[Steps.BackgroundPattern], [wizSnap.emblemData])
  const scrolling = useMemo(() => wizSnap.emblemData?.[Steps.Scrolling], [wizSnap.emblemData])
  const sewingGuideColor = useMemo(() => wizSnap.emblemData?.[Steps.SewingGuideColor], [wizSnap.emblemData])
  const emblemLeather = useMemo(() => wizSnap.emblemData?.[Steps.EmblemLeather], [wizSnap.emblemData])
  const combination = useMemo(() => wizSnap.emblemData?.[Steps.Combination], [wizSnap.emblemData])
  const emblemPrintStitch = useMemo(() => wizSnap.emblemData?.[Steps.EmblemPrintStitch], [wizSnap.emblemData])
  const emblemPrintStitchOption = useMemo(() => wizSnap.designRequest?.productOptions['emblemPrintStitch'], [wizSnap.designRequest])

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const qty = parseInt(e.target.value, 10) || 0;
    wizardStore.pricing.quantity = qty;
  };

  const pricingLoaded = !isEmpty(wizSnap.pricing);

  return (
    <React.Fragment>
      <div className={`flex ${matchMedia ? 'flex-col' : ''} min-h-screen`}>
        <div className="w-full md:w-1/2 p-4">
          <div className="mb-4">
            <h4 className="uppercase font-bold text-lg my-4">General Information</h4>
            <div className="dashed-title my-2" />
          </div>
          <div className="flex gap-4">
            <GeneralInfo />
          </div>
        </div>
        <div className="w-full md:w-1/2 p-4">
          <div className="mb-4">
            <h4 className="uppercase font-bold text-lg my-4">Order Summary</h4>
            <div className="dashed-title my-2" />
          </div>
          <h2 className="font-bold text-md mb-4 uppercase">Product Details</h2>
          <div className="flex gap-4">
            {designTypeImage ? (
              <div className="w-20 h-20">
                <img src={designTypeImage.imageSrc} className="h-full w-full" alt="design-type-img" />
              </div>
            ) : null}
            {holographicUploadImage ? (
              <div className="w-20 h-20">
                <img src={holographicUploadImage.imageSrc} className="h-full w-full" alt="holographic-img" />
              </div>
            ) : null}
          </div>
          <div className="flex flex-col gap-2 mt-4 text-left">
            <p>
              <strong>Quantity: </strong>
              <input
                type="number"
                min="0"
                disabled={!pricingLoaded}
                className="border border-gray-300 rounded px-2 py-1"
                value={wizSnap.pricing.quantity ?? 0}
                onChange={handleQuantityChange}
                style={{ width: "4rem" }}
              />
            </p>
            {!pricingLoaded
              ? <Loading />
              : <p><strong>Price: $</strong>{wizSnap.pricing.price * wizSnap.pricing.quantity}</p>
            }
            <p><strong>Product Type: </strong> {wizSnap.emblemData[Steps.ProductType].name}</p>
            <p><strong>Product Subtype: </strong> {wizSnap.emblemData[Steps.ProductSubtype].name}</p>
            {shape ? <p><strong>Shape: </strong> {shape.name}</p> : null}
            {sizeStandard ? (
              <p><strong>Size: </strong>
                <>
                  {sizeStandard.code ? `${sizeStandard.name} - ` : null}
                  {sizeStandard.width ? `Width: ${sizeStandard.width}"` : null},
                  {sizeStandard.height ? ` Height: ${sizeStandard.height}"` : null}
                  {sizeStandard.scaleHeight ? " Height: Scale Height" : null}
                  {sizeStandard.scaleWidth ? " Width: Scale Width" : null}
                </>
              </p>
            ) : null}
            {combination ? (
              <p className="flex gap-2 items-center"><strong>Combination:</strong>
                {combination?.image?.url ?
                  <img src={`${IMAGE_SERVER}${combination?.image?.url}`} alt="fabric_color_img" className="rounded-full w-5 h-5 border border-slate-300" />
                  :
                  <div className="rounded-full w-5 h-5 border border-slate-300" style={{ ...({ backgroundColor: combination.htmlColor }) }}></div>
                }
                {combination.name}
              </p>
            ) : null}
            {fabricColor ? (
              <p className="flex gap-2 items-center"><strong>Fabric/Media Color:</strong>
                {fabricColor?.image?.url ?
                  <img src={`${IMAGE_SERVER}${fabricColor?.image?.url}`} alt="fabric_color_img" className="rounded-full w-5 h-5 border border-slate-300" />
                  :
                  <div className="rounded-full w-5 h-5 border border-slate-300" style={{ ...({ backgroundColor: fabricColor.htmlColor }) }}></div>
                }
                {fabricColor.name}
              </p>
            ) : null}
            {fabricType ? (
              <p><strong>Fabric Type: </strong>{fabricType.name}</p>
            ) : null}
            {backing ? (
              <p><strong>Backing: </strong>{backing.name}</p>
            ) : null}
            {borderType ? (
              <p><strong>Border Type: </strong>{borderType.name}</p>
            ) : null}
            {borderColor ? (
              <p><strong>Border Color: </strong>{borderColor.name}</p>
            ) : null}
            {layout ? (
              <p><strong>Layout: </strong>{layout.name}</p>
            ) : null}
            {scrolling ? (
              <p><strong>Scrolling Font: </strong>{scrolling.scrollingFontDisplayName}</p>
            ) : null}
            {scrolling ? (
              <p><strong>Scrolling Text: </strong>{scrolling.scrollingText}</p>
            ) : null}
            {sewingGuideColor ? (
              <p className="flex gap-2 items-center"><strong>Sewing Guide Color:</strong>
                <div
                  className="rounded-full w-5 h-5 border border-slate-300"
                  style={{ ...(sewingGuideColor?.htmlColor ? { backgroundColor: sewingGuideColor.htmlColor } : { backgroundImage: `url('${IMAGE_SERVER}${sewingGuideColor?.image?.url}')` }) }}>
                </div>
                {sewingGuideColor.name}
              </p>
            ) : null}
            {bgPattern ? (
              <p><strong>Background Pattern: </strong>{bgPattern.name}</p>
            ) : null}
            {designTypeImage ? (
              <p><strong>Design Type: </strong> Upload Image</p>
            ) : null}
            {specialInstructions ? (
              <p><strong>Special Instructions: </strong>{specialInstructions}</p>
            ) : null}
            {emblem3d?.items?.length ? (
              <>
                <div className="flex flex-col gap-2 ">
                  {emblem3d.items.map((item: CircleData, index: number) => (
                    <div key={index} className="flex gap-2 capitalize">
                      <strong>3D Indication {index + 1}</strong>
                      {item.dimension}, {item.option?.name}
                      {item?.instructions && (
                        <>, <strong>{item.instructions}</strong></>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : null}
            {emblemLeather?.indicators?.length ? (
              <>
                <div className="flex flex-col gap-2 ">
                  {emblemLeather.indicators.map((item: LeatherIndicators, index: number) => (
                    <div key={index} className="flex gap-2 capitalize">
                      <strong>Leather Indication {index + 1}:</strong>
                      {item.indicator}
                      {item?.instructions && (
                        <>, <strong>{item.instructions}</strong></>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : null}
            {emblemPrintStitch ? (
              <>
                <div className="flex flex-col gap-2 ">
                  {emblemPrintStitch.items.map((item: CircleData, index: number) => (
                    <div key={index} className="flex gap-2 capitalize">
                      <strong>Print Stitch Indication {index + 1}:</strong>
                      {item.option?.name}
                      {item?.instructions && (
                        <>, <strong>{item.instructions}</strong></>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : null}
            {(!emblemPrintStitch || !emblemPrintStitch?.items.length) &&
              emblemPrintStitchOption?.setEmblemPrintStitchOption === "AllowWEIToChoose"
              ? (<p><strong>Print Stitch Indications: </strong>Allow Stahls Design Team to Choose</p>
              ) : null
            }
            {colors?.items?.length ? (
              <>
                <div className="dashed-title my-2"></div>
                <h2 className="font-bold text-md mb-3">Colors</h2>
                <h2 className="font-bold text-xs mb-3">{colors.items.length} COLORS SELECTED</h2>
                <div className="flex flex-col gap-2">
                  {colors.items.map(({ option }: { option: StepOption }) => (
                    <div key={option.id} className="flex gap-2">
                      <div
                        className="rounded-full w-5 h-5 border border-slate-300"
                        style={{ ...(option?.htmlColor ? { backgroundColor: option.htmlColor } : { backgroundImage: `url('${IMAGE_SERVER}${option?.image?.url}')` }) }}>
                      </div>
                      {option.name} {option?.instructions && `- ${option?.instructions}`}
                    </div>
                  ))}
                </div>
              </>
            ) : null}
            {colors.allowWEItoChoose && colors?.items?.length === 0 ?
              (<>
                <div className="dashed-title my-2"></div>
                <h2 className="font-bold text-md mb-3">Colors</h2>
                <div className="flex flex-col gap-2">
                  The Stahls Design Team will select the colors for this design.
                </div>
              </>
              ) : null
            }
            {flipColors ? (
              <>
                <div className="dashed-title my-2"></div>
                <h2 className="font-bold text-md mb-3">Flip Image Colors</h2>
                <h2 className="font-bold text-xs mb-3">{flipColors.items.length} COLORS SELECTED</h2>
                <div className="flex flex-col gap-2 ">
                  {flipColors.items.map(({ option }: { option: StepOption }) => (
                    <div key={option.id} className="flex gap-2">
                      <div
                        className="rounded-full w-5 h-5 border border-slate-300"
                        style={{ ...(option?.htmlColor ? { backgroundColor: option.htmlColor } : { backgroundImage: `url('${IMAGE_SERVER}${option?.image?.url}')` }) }}>
                      </div>
                      {option.name}
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}
